import { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown, Download } from 'lucide-react';
import { motion } from 'framer-motion';

// ─── Renewable energy aerial video (wind turbines + solar) ───────────────────
// To change the video: replace the YouTube video ID below (11-char string after ?v=)
const YOUTUBE_VIDEO_ID = 'y8chJCY81Vs';
const HERO_BG_FALLBACK  = 'https://images.pexels.com/photos/21905739/pexels-photo-21905739.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920';

// Animation constants — extracted to avoid new object refs on every render
const ANIM_LABEL    = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.2 } };
const ANIM_TITLE    = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 0.35 } };
const ANIM_SUBTITLE = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.55 } };
const ANIM_BUTTONS  = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.75 } };
const ANIM_DECK     = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.95 } };

const VIDEO_STYLE = {
  position: 'absolute', top: '50%', left: '50%',
  width: '100vw', height: '56.25vw',
  minHeight: '100vh', minWidth: '177.78vh',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none', opacity: 0.48,
};

function HeroBackground({ isMobile, bgLoaded }) {
  if (!isMobile) {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=0&disablekb=1&iv_load_policy=3&modestbranding=1&rel=0&showinfo=0&vq=hd1080`}
          title="KÖBER Energy Holding — background video"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          aria-hidden="true"
          style={VIDEO_STYLE}
        />
      </div>
    );
  }
  return (
    <div
      className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${bgLoaded ? 'opacity-100' : 'opacity-0'}`}
      style={{ backgroundImage: `url('${HERO_BG_FALLBACK}')`, opacity: 0.52 }}
    />
  );
}

function HeroButtons({ text, onContact }) {
  return (
    <>
      <motion.div {...ANIM_BUTTONS} className="flex flex-col sm:flex-row gap-4">
        <button
          data-testid="hero-contact-btn"
          onClick={onContact}
          className="group inline-flex items-center justify-center gap-3 bg-[#D4A017] hover:bg-[#F5B814] text-black font-semibold px-8 py-4 text-sm tracking-wide transition-all duration-200 btn-gold-pulse"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {text.cta}
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
        </button>
        <button
          data-testid="hero-projects-btn"
          onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
          className="inline-flex items-center justify-center gap-3 border border-white/25 text-white hover:border-[#D4A017]/60 hover:text-[#D4A017] px-8 py-4 text-sm tracking-wide transition-all duration-200"
        >
          {text.cta2}
        </button>
      </motion.div>
      <motion.div {...ANIM_DECK} className="mt-4">
        <a
          href="/KOBER-Energy-Investment-Deck.pdf"
          download="KOBER-Energy-Investment-Deck.pdf"
          data-testid="hero-deck-download-btn"
          className="group inline-flex items-center gap-2.5 text-sm text-white/50 hover:text-[#D4A017] transition-colors duration-200 border-b border-white/15 hover:border-[#D4A017]/40 pb-0.5"
        >
          <Download size={13} className="group-hover:-translate-y-0.5 transition-transform duration-200" />
          {text.deckCta}
        </a>
      </motion.div>
    </>
  );
}

function HeroContent({ text, onContact }) {
  return (
    <div className="relative z-10 container mx-auto px-6 lg:px-12 max-w-7xl">
      <div className="max-w-3xl">
        <motion.p {...ANIM_LABEL} className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-6 font-medium">
          {text.label}
        </motion.p>
        <motion.h1
          {...ANIM_TITLE}
          className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-white leading-tight tracking-tight mb-6"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {text.title}
        </motion.h1>
        <motion.p {...ANIM_SUBTITLE} className="text-base sm:text-lg text-white/65 leading-relaxed mb-10 max-w-2xl">
          {text.subtitle}
        </motion.p>
        <HeroButtons text={text} onContact={onContact} />
      </div>
    </div>
  );
}

export default function HeroSection({ text, onContact }) {
  const [isMobile, setIsMobile] = useState(false);
  const [bgLoaded, setBgLoaded]  = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
    // check is defined inside this effect; setIsMobile is a stable useState dispatcher
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = HERO_BG_FALLBACK;
    img.onload = () => setBgLoaded(true);
    // HERO_BG_FALLBACK is a module constant; img is created inside this effect;
    // setBgLoaded is a stable useState dispatcher
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-start overflow-hidden bg-[#0a0a0a]">
      <HeroBackground isMobile={isMobile} bgLoaded={bgLoaded} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/30" />
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#D4A017] via-[#D4A017]/40 to-transparent" />
      <HeroContent text={text} onContact={onContact} />
      <button
        onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
        data-testid="scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/35 hover:text-[#D4A017] transition-colors duration-200"
      >
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <ChevronDown size={18} className="animate-bounce" />
      </button>
    </section>
  );
}
